/**
 * 选项图片尺寸工具
 *
 * 功能逻辑：
 * 1. 遍历选项列表，解析描述字段（默认 optionDesc）中的图片资源
 * 2. 使用离屏 Image 读取 naturalWidth / naturalHeight，避免插入 DOM 造成布局抖动
 * 3. 计算每张图的宽高比 aspectRatio = width / height
 * 4. 取所有成功加载图片的最大宽度，作为统一展示宽度
 * 5. 加载失败、缺失 src、尺寸非法时跳过该图，不影响其余图片
 * 6. 按 URL 缓存测量结果，选项动态增删时只测量新增图片
 *
 * 依赖：浏览器 Image、Promise；无 Vue / 业务接口依赖，可在任意组件中引用。
 *
 * @example
 * import { measureOptionImages, buildOptionImgStyle } from '@assets/utils/optionImageSize'
 *
 * measureOptionImages(this.data.options).then((result) => {
 *   this.optionImgMaxWidth = result.maxWidth
 *   this.optionImgStyle = buildOptionImgStyle(result.maxWidth)
 * })
 *
 * @example
 * // 旧版题目结构（itemList + itemDes）
 * measureOptionImages(this.data.itemList, { descKey: 'itemDes' })
 */

/** @type {Object<string, Promise<OptionImageInfo|OptionImageFail>>} */
const imageSizeCache = Object.create(null)

/**
 * @typedef {Object} OptionImageInfo
 * @property {string} src 图片地址
 * @property {number} naturalWidth 原始宽度
 * @property {number} naturalHeight 原始高度
 * @property {number} aspectRatio 宽高比（width / height）
 */

/**
 * @typedef {Object} OptionImageFail
 * @property {string} src 图片地址
 * @property {string} reason 失败原因：missing | parse-error | timeout | load-failed | invalid-size
 */

/**
 * @typedef {Object} OptionImageMeasureResult
 * @property {number} maxWidth 所有成功加载图片的最大宽度（px），无可用图片时为 0
 * @property {OptionImageInfo[]} images 成功测量的图片信息
 * @property {Object<string, OptionImageInfo>} imageMap 以 src 为 key 的图片信息映射
 * @property {OptionImageFail[]} failed 加载失败或无效的图片
 */

/**
 * @typedef {Object} OptionImageSizeConfig
 * @property {string} [descKey='optionDesc'] 选项描述字段名，值为 JSON 字符串或片段数组
 * @property {string} [imgKey='img'] 片段中的图片字段名
 * @property {number} [maxCap=0] 展示宽度上限（px），0 表示不限制
 * @property {number} [timeout=15000] 单张图片加载超时时间（ms）
 */

/**
 * 从选项列表中收集去重后的图片地址
 * @method collectOptionImageSrcs
 * @param {Array<Object>} options 选项列表，如 data.options
 * @param {OptionImageSizeConfig} [config]
 * @returns {string[]}
 */
export const collectOptionImageSrcs = (options, config) => {
  const imgKey = (config && config.imgKey) || 'img'
  const preferred = config && config.descKey
  const descKeys = []
  if (preferred) descKeys.push(preferred)
  // 兼容新版 optionDesc、旧版 itemDes，避免只配 mixin 时漏采图片
  ;['optionDesc', 'itemDes'].forEach((key) => {
    if (descKeys.indexOf(key) === -1) descKeys.push(key)
  })
  const srcs = []
  const seen = Object.create(null)
  if (!Array.isArray(options)) return srcs

  for (let i = 0; i < options.length; i++) {
    const item = options[i]
    if (!item) continue
    for (let k = 0; k < descKeys.length; k++) {
      let fragments = item[descKeys[k]]
      if (fragments == null) continue
      if (typeof fragments === 'string') {
        try {
          fragments = JSON.parse(fragments || '[]')
        } catch (e) {
          continue
        }
      }
      if (!Array.isArray(fragments)) continue
      for (let j = 0; j < fragments.length; j++) {
        const part = fragments[j]
        const src = part && part[imgKey]
        if (typeof src === 'string' && src && !seen[src]) {
          seen[src] = true
          srcs.push(src)
        }
      }
    }
  }
  return srcs
}

/**
 * 离屏加载单张图片并读取原始尺寸（带缓存）
 * @method loadImageNaturalSize
 * @param {string} src 图片地址
 * @param {number} [timeout=15000] 超时时间（ms）
 * @returns {Promise<OptionImageInfo|OptionImageFail>}
 */
export const loadImageNaturalSize = (src, timeout) => {
  const wait = timeout || 15000
  if (!src || typeof src !== 'string') {
    return Promise.resolve({ src: src || '', reason: 'missing' })
  }
  if (imageSizeCache[src]) return imageSizeCache[src]

  const task = new Promise((resolve) => {
    const img = new Image()
    let settled = false
    const finish = (result) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(result)
    }
    const timer = setTimeout(() => {
      finish({ src, reason: 'timeout' })
    }, wait)

    img.onload = () => {
      const naturalWidth = img.naturalWidth || 0
      const naturalHeight = img.naturalHeight || 0
      if (!naturalWidth || !naturalHeight) {
        finish({ src, reason: 'invalid-size' })
        return
      }
      finish({
        src,
        naturalWidth,
        naturalHeight,
        aspectRatio: naturalWidth / naturalHeight
      })
    }
    img.onerror = () => {
      finish({ src, reason: 'load-failed' })
    }
    img.src = src
  })

  imageSizeCache[src] = task
  task.then((result) => {
    // 失败结果不长期缓存，便于后续重试（例如动态补图、临时网络错误）
    if (result && result.reason) {
      delete imageSizeCache[src]
    }
  })
  return task
}

/**
 * 批量测量图片尺寸，并计算统一展示宽度
 * @method measureImageSizes
 * @param {string[]} srcs 图片地址列表
 * @param {OptionImageSizeConfig} [config]
 * @returns {Promise<OptionImageMeasureResult>}
 */
export const measureImageSizes = (srcs, config) => {
  const list = Array.isArray(srcs) ? srcs.filter(Boolean) : []
  const timeout = (config && config.timeout) || 15000
  const maxCap = (config && config.maxCap) || 0

  if (!list.length) {
    return Promise.resolve({
      maxWidth: 0,
      images: [],
      imageMap: {},
      failed: []
    })
  }

  return Promise.all(list.map((src) => loadImageNaturalSize(src, timeout))).then((results) => {
    const images = []
    const failed = []
    const imageMap = {}
    let maxWidth = 0

    for (let i = 0; i < results.length; i++) {
      const item = results[i]
      if (!item || item.reason || !item.naturalWidth) {
        failed.push({
          src: (item && item.src) || list[i] || '',
          reason: (item && item.reason) || 'load-failed'
        })
        continue
      }
      images.push(item)
      imageMap[item.src] = item
      if (item.naturalWidth > maxWidth) maxWidth = item.naturalWidth
    }

    if (maxCap > 0 && maxWidth > maxCap) maxWidth = maxCap

    return { maxWidth, images, imageMap, failed }
  })
}

/**
 * 测量选项列表中的图片尺寸，返回统一展示宽度
 * @method measureOptionImages
 * @param {Array<Object>} options 选项列表，如 data.options
 * @param {OptionImageSizeConfig} [config]
 * @returns {Promise<OptionImageMeasureResult>}
 *
 * @example
 * // 在组件 methods 中调用
 * measureOptionImages(this.data.options).then((res) => {
 *   this.optionImgMaxWidth = res.maxWidth
 * })
 */
export const measureOptionImages = (options, config) => {
  try {
    const srcs = collectOptionImageSrcs(options, config)
    return measureImageSizes(srcs, config)
  } catch (e) {
    return Promise.resolve({
      maxWidth: 0,
      images: [],
      imageMap: {},
      failed: [{ src: '', reason: 'parse-error' }]
    })
  }
}

/**
 * 根据统一宽度生成 img 内联样式，高度自适应以保持原始宽高比
 * @method buildOptionImgStyle
 * @param {number} maxWidth 统一展示宽度（px）
 * @param {number} [aspectRatio] 单张图宽高比，传入后可设置 aspect-ratio 减少布局偏移
 * @returns {Object} 可用于 :style 的样式对象
 *
 * @example
 * <img :src="oSon.img" :style="buildOptionImgStyle(optionImgMaxWidth, ratio)">
 */
export const buildOptionImgStyle = (maxWidth, aspectRatio) => {
  const style = {
    height: 'auto',
    maxWidth: '100%'
  }
  if (maxWidth > 0) {
    style.width = maxWidth + 'px'
  } else {
    style.width = 'auto'
  }
  if (aspectRatio > 0) {
    style.aspectRatio = String(aspectRatio)
  }
  return style
}

/**
 * 清除图片尺寸缓存
 * @method clearOptionImageSizeCache
 * @param {string} [src] 传入则只清该地址；不传则清空全部
 */
export const clearOptionImageSizeCache = (src) => {
  if (src) {
    delete imageSizeCache[src]
    return
  }
  Object.keys(imageSizeCache).forEach((key) => {
    delete imageSizeCache[key]
  })
}



// WEBPACK FOOTER //
// ./src/assets/utils/optionImageSize.js